/**
 * The wire format between the app's sync engine and the Sync Worker (issue #24, ADR-0017).
 *
 * Vocabulary (CONTEXT.md, "Delivery"): an Account holds a person's Characters, Bookmarks and
 * Recents; a Device is one signed-in install, named at sign-in and holding a Device Token; Sync
 * pushes after each save and pulls at launch; the loser of a conflict comes back to be kept as a
 * Conflict Copy; a deleted document stays thirty days as a tombstone so Recently Deleted can
 * undo it.
 *
 * Both sides import this file: `server/` writes the responses and `src/sync/` (issue #25) reads
 * them. Types only, so it stays free of Worker and DOM globals.
 */

/** Bump when the shape of a request or a response changes incompatibly. */
export const SYNC_FORMAT = 1 as const;

/** Everything an Account syncs. Per-device settings are never sent. */
export const SYNC_COLLECTIONS = ["character", "bookmark", "recent"] as const;
export type SyncCollection = (typeof SYNC_COLLECTIONS)[number];

export function isSyncCollection(value: unknown): value is SyncCollection {
  return typeof value === "string" && (SYNC_COLLECTIONS as readonly string[]).includes(value);
}

/** What names one document within an Account. Ids are the app's own (a Character's id, a UUID). */
export interface DocumentRef {
  collection: SyncCollection;
  id: string;
}

/**
 * One document as the Account holds it. `version` is assigned by the server: 1 at the first
 * write and one higher at every write after, so a Device that pushes the version it last saw
 * says exactly which write it is answering.
 */
export interface SyncDocument extends DocumentRef {
  version: number;
  /** The writing Device's clock when the person saved (ms since the epoch). Decides conflicts. */
  updatedAt: number;
  /** The Device that wrote it, or null for a document written before Devices were recorded. */
  deviceId: string | null;
  /** When it was deleted, or null while it is live. A tombstone keeps its body for thirty days. */
  deletedAt: number | null;
  /** The document body, exactly as the Device sent it. */
  data: unknown;
}

/** The version of a losing record is null when the server never held it. */
export interface ConflictLoser extends Omit<SyncDocument, "version"> {
  version: number | null;
}

export interface PushDocument extends DocumentRef {
  /** The version the Device last saw, or null for a document it has never synced. */
  baseVersion: number | null;
  updatedAt: number;
  /** A delete keeps the stored body under a tombstone; `data` may then be omitted. */
  deleted?: boolean;
  data?: unknown;
}

export interface PushRequest {
  documents: PushDocument[];
}

/**
 * What became of one pushed document. `stored` means the Account took it at `version`.
 * `conflict` means two Devices changed it apart: the later `updatedAt` is the winner and holds
 * the Account, the loser comes back in full to be kept as a Conflict Copy. Ties keep the
 * record the Account already held. Nothing is ever discarded silently.
 */
export type PushResult =
  | ({ status: "stored"; version: number } & DocumentRef)
  | ({ status: "conflict"; winner: SyncDocument; loser: ConflictLoser } & DocumentRef);

export interface PushResponse {
  results: PushResult[];
}

/** The pull cursor: an opaque string a Device keeps and sends back. "" starts from the beginning. */
export type SyncCursor = string;

export interface PullResponse {
  /** Everything changed after the cursor, tombstones included, oldest change first. */
  documents: SyncDocument[];
  cursor: SyncCursor;
  /** True when the page filled: pull again with the new cursor. */
  more: boolean;
}

export interface DeletedResponse {
  documents: SyncDocument[];
  cursor: SyncCursor;
  more: boolean;
}

export interface RestoreRequest {
  documents: DocumentRef[];
}

export type RestoreResult =
  | ({ status: "restored"; document: SyncDocument } & DocumentRef)
  /** No tombstone: it was purged after thirty days, or never deleted. */
  | ({ status: "missing" } & DocumentRef);

export interface RestoreResponse {
  results: RestoreResult[];
}

export interface DeviceSummary {
  id: string;
  name: string;
  createdAt: number;
  lastSeenAt: number;
  /** The Device making the request. */
  current: boolean;
}

export interface AccountUsage {
  documents: number;
  /** The sum of the document bodies as the Devices sent them, before the server compresses them. */
  bytes: number;
  ceilings: typeof SYNC_CEILINGS;
}

export interface AccountResponse {
  account: {
    id: string;
    /** The issuer that vouched for the person: "google" today, "email" when #27 lands. */
    issuer: string;
    email: string | null;
    name: string | null;
    createdAt: number;
  };
  devices: DeviceSummary[];
  usage: AccountUsage;
}

export interface SignInResponse {
  /** The Device Token. Opaque, non-expiring, sent as `Authorization: Bearer <token>`. */
  token: string;
  account: AccountResponse["account"];
  device: DeviceSummary;
}

export interface DeleteAccountTokenResponse {
  confirmation: string;
  expiresAt: number;
}

/** The most documents one push may carry. A Device with more to send pushes again. */
export const SYNC_MAX_PUSH = 100;

/** The most documents one pull or one listing hands back, and the default when none is asked for. */
export const SYNC_PAGE = 200;

/** The ceilings an Account is held to (ADR-0017). Refusals name the one that was hit. */
export const SYNC_CEILINGS = {
  /** Per document, measured on the body the Device sent. */
  documentBytes: 2_000_000,
  /** Live documents and tombstones together. */
  documents: 5_000,
  /** The same measure as `documentBytes`, summed over the Account. */
  accountBytes: 200_000_000,
  /** Per Device Token, in a fixed minute. */
  requestsPerMinute: 60,
} as const;

export type SyncErrorCode =
  /** No Device Token, or one that was rotated away or revoked. Nothing was written. */
  | "unauthorized"
  /** Over `requestsPerMinute`; `retryAfter` says how many seconds to wait. */
  | "rate_limited"
  /** The request was not shaped as this file says. */
  | "bad_request"
  /** One document is over `documentBytes`; the whole push was refused. */
  | "document_too_large"
  /** The Account is at `documents`; the whole push was refused. */
  | "account_document_limit"
  /** The Account is at `accountBytes`; the whole push was refused. */
  | "account_byte_limit"
  /**
   * A body that stays over the row a document is stored in even after compression. Reachable
   * only with incompressible data well past a Character's size (README, "Sync Worker").
   */
  | "document_not_storable"
  /** The route or the document is not there. */
  | "not_found"
  /** A secret the Worker needs is missing; it refuses rather than running half-configured. */
  | "server_misconfigured"
  /** Sign-in did not finish: the person refused, or the state expired. */
  | "sign_in_failed";

export interface SyncErrorBody {
  error: {
    code: SyncErrorCode;
    message: string;
    /** The ceiling that was hit, when one was. */
    ceiling?: keyof typeof SYNC_CEILINGS;
    /** The document that caused a refusal, when one did. */
    document?: DocumentRef;
    /** Seconds to wait, on `rate_limited`. */
    retryAfter?: number;
    /** The names of the missing secrets, on `server_misconfigured`. */
    missing?: string[];
  };
}

/** Where the Worker sends the browser when sign-in ends. The app reads `claim` or `error`. */
export const SIGN_IN_RETURN_ROUTE = "#/signin";

/** How long a claim from the sign-in redirect may be held before it is spent (ms). */
export const CLAIM_LIFETIME_MS = 5 * 60_000;

/** How long a tombstone keeps its body before the purge (ms). */
export const TOMBSTONE_LIFETIME_MS = 30 * 24 * 60 * 60_000;
